import React, { useState, useRef, useEffect } from "react";
import { Sparkles, LogOut, User as UserIcon, Ticket, LayoutDashboard, Crown, FileText, Globe, ChevronDown, CalendarDays, Compass } from "lucide-react";
import { translations } from "@/i18n";

const MENU_LABELS = {
  en: { my_cvs: "My CVs", redeem: "Redeem promo code", admin: "Admin Dashboard", logout: "Log out", premium: "Premium", signin: "Sign in", visa_days: "88 Days Tracker", guide_wa: "WA Guide" },
  fr: { my_cvs: "Mes CV", redeem: "Utiliser un code promo", admin: "Tableau de bord Admin", logout: "Se déconnecter", premium: "Premium", signin: "Connexion", visa_days: "Compteur 88 jours", guide_wa: "Guide WA" },
  de: { my_cvs: "Meine Lebensläufe", redeem: "Promo-Code einlösen", admin: "Admin-Dashboard", logout: "Abmelden", premium: "Premium", signin: "Anmelden", visa_days: "88-Tage-Tracker", guide_wa: "WA Guide" },
  it: { my_cvs: "I miei CV", redeem: "Riscatta codice promo", admin: "Dashboard Admin", logout: "Esci", premium: "Premium", signin: "Accedi", visa_days: "Tracker 88 giorni", guide_wa: "Guida WA" },
  es: { my_cvs: "Mis CV", redeem: "Canjear código promo", admin: "Panel de Admin", logout: "Cerrar sesión", premium: "Premium", signin: "Entrar", visa_days: "Contador 88 días", guide_wa: "Guía WA" },
  pt: { my_cvs: "Os meus CVs", redeem: "Resgatar código promo", admin: "Painel Admin", logout: "Sair", premium: "Premium", signin: "Entrar", visa_days: "Contador 88 dias", guide_wa: "Guia WA" },
};

// Short/long labels for the header 88 Days button
const VISA_BTN = {
  en: { short: "88 Days", long: "88 Days Tracker" },
  fr: { short: "88 jours", long: "Compteur 88 jours" },
  de: { short: "88 Tage", long: "88-Tage-Tracker" },
  it: { short: "88 giorni", long: "Tracker 88 giorni" },
  es: { short: "88 días", long: "Contador 88 días" },
  pt: { short: "88 dias", long: "Contador 88 dias" },
};

const LANGS = [
  { code: "en", label: "EN", full: "English" },
  { code: "fr", label: "FR", full: "Français" },
  { code: "de", label: "DE", full: "Deutsch" },
  { code: "it", label: "IT", full: "Italiano" },
  { code: "es", label: "ES", full: "Español" },
  { code: "pt", label: "PT", full: "Português" },
];

export function Header({ lang, setLang, onCta, onVisaDays, user, onLogin, onLogout, onOpenRedeem, onOpenAdmin, onOpenMyCVs, onOpenUpgrade, onOpenGuideWA, onHome }) {
  const ml = MENU_LABELS[lang] || MENU_LABELS.en;
  const [menuOpen, setMenuOpen] = useState(false);
  const [langMobileOpen, setLangMobileOpen] = useState(false);
  const menuRef = useRef(null);
  const langMobileRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (langMobileRef.current && !langMobileRef.current.contains(e.target)) setLangMobileOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const currentLang = LANGS.find((l) => l.code === lang) || LANGS[0];
  const visaBtn = VISA_BTN[lang] || VISA_BTN.en;

  return (
    <header className="glass sticky top-0 z-50" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
        {/* Logo */}
        <button
          type="button"
          onClick={() => { onHome ? onHome() : (window.location.hash = "#top"); }}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity focus:outline-none flex-shrink-0"
          data-testid="logo-link"
          aria-label="AussieCV — Home"
        >
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-[#D95D39] flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="font-heading font-extrabold text-base sm:text-xl tracking-tight text-[#292524]">
            AussieCV
          </div>
        </button>

        <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
          {/* 88 Days Tracker — proper text button (mobile: short, desktop: full) */}
          {onVisaDays && (
            <button
              onClick={onVisaDays}
              className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-bold rounded-lg sm:rounded-xl bg-[#2A5C5A] text-white hover:bg-[#1F4644] transition-colors shadow-sm whitespace-nowrap"
              data-testid="header-visa-days-btn"
              title={visaBtn.long}
            >
              <CalendarDays className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              <span className="md:hidden">{visaBtn.short}</span>
              <span className="hidden md:inline">{visaBtn.long}</span>
            </button>
          )}

          {/* Guide WA — desktop nav link (in dropdown for mobile) */}
          {onOpenGuideWA && (
            <button
              onClick={onOpenGuideWA}
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 text-sm font-bold rounded-xl border border-[#EAE5DB] bg-white text-[#292524] hover:border-[#D95D39] hover:text-[#D95D39] transition-colors whitespace-nowrap"
              data-testid="header-guide-wa-btn"
              title={ml.guide_wa}
            >
              <Compass className="w-4 h-4 flex-shrink-0" />
              {ml.guide_wa}
            </button>
          )}

          {/* Language switcher — inline on desktop */}
          <div className="hidden md:flex items-center gap-0.5 bg-white/60 border border-[#EAE5DB] rounded-xl p-1" data-testid="lang-switcher">
            {LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                data-testid={`lang-${l.code}`}
                className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  lang === l.code
                    ? "bg-[#292524] text-white"
                    : "text-[#57534E] hover:bg-[#F2EBE1]"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          {/* Language switcher — compact dropdown on mobile/tablet */}
          <div className="relative md:hidden" ref={langMobileRef}>
            <button
              onClick={() => setLangMobileOpen((s) => !s)}
              className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-semibold rounded-lg bg-white/60 border border-[#EAE5DB] text-[#292524] hover:bg-white transition-colors"
              data-testid="lang-mobile-toggle"
              aria-label="Change language"
            >
              <Globe className="w-3.5 h-3.5" />
              <span>{currentLang.label}</span>
              <ChevronDown className="w-3 h-3" />
            </button>
            {langMobileOpen && (
              <div
                className="absolute right-0 top-full mt-2 min-w-[140px] bg-white border border-[#EAE5DB] rounded-xl shadow-2xl overflow-hidden z-50"
                data-testid="lang-mobile-menu"
              >
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setLangMobileOpen(false); }}
                    data-testid={`lang-mobile-${l.code}`}
                    className={`w-full text-left px-3 py-2.5 text-sm flex items-center justify-between gap-2 transition-colors ${
                      lang === l.code ? "bg-[#FAF6F0] font-bold text-[#292524]" : "text-[#57534E] hover:bg-[#FAF6F0]"
                    }`}
                  >
                    <span>{l.full}</span>
                    <span className="text-[10px] font-mono opacity-60">{l.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Optimize my CV CTA — DESKTOP ONLY (hero already has one on mobile) */}
          <button
            onClick={onCta}
            className="!hidden md:!inline-flex btn-primary !py-2.5 !px-4 text-sm whitespace-nowrap"
            data-testid="header-cta-btn"
          >
            <Sparkles className="w-4 h-4" />
            {translations[lang].nav.cta}
          </button>

          {/* Auth */}
          {user ? (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((s) => !s)}
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#F2EBE1] border border-[#EAE5DB] overflow-hidden flex items-center justify-center hover:ring-2 hover:ring-[#D95D39]/40 transition flex-shrink-0"
                data-testid="header-user-avatar"
                title={user.name || user.email}
                aria-label="Account menu"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <UserIcon className="w-4 h-4 text-[#57534E]" />
                )}
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-64 bg-white border border-[#EAE5DB] rounded-2xl shadow-2xl overflow-hidden z-50"
                  data-testid="header-user-menu"
                >
                  <div className="px-4 py-3 border-b border-[#F2EBE1] bg-[#FAF6F0]">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm text-[#292524] truncate">
                          {user.name || "Backpacker"}
                        </div>
                        <div className="text-xs text-[#78716C] truncate">{user.email}</div>
                      </div>
                      {user.is_premium && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E5A93D] text-white text-[10px] font-bold whitespace-nowrap">
                          <Crown className="w-3 h-3" /> {ml.premium}
                        </span>
                      )}
                    </div>
                  </div>
                  {onVisaDays && (
                    <button
                      onClick={() => { setMenuOpen(false); onVisaDays(); }}
                      className="w-full text-left px-4 py-3 text-sm text-[#292524] hover:bg-[#FAF6F0] flex items-center gap-2 border-b border-[#F2EBE1]"
                      data-testid="header-menu-visa-days-btn"
                    >
                      <CalendarDays className="w-4 h-4 text-[#2A5C5A]" /> {ml.visa_days}
                    </button>
                  )}
                  {onOpenGuideWA && (
                    <button
                      onClick={() => { setMenuOpen(false); onOpenGuideWA(); }}
                      className="w-full text-left px-4 py-3 text-sm text-[#292524] hover:bg-[#FAF6F0] flex items-center gap-2 border-b border-[#F2EBE1] md:hidden"
                      data-testid="header-menu-guide-wa-btn"
                    >
                      <Compass className="w-4 h-4 text-[#D95D39]" /> {ml.guide_wa}
                    </button>
                  )}
                  {onOpenMyCVs && (
                    <button
                      onClick={() => { setMenuOpen(false); onOpenMyCVs(); }}
                      className="w-full text-left px-4 py-3 text-sm text-[#292524] hover:bg-[#FAF6F0] flex items-center gap-2 border-b border-[#F2EBE1]"
                      data-testid="header-mycvs-btn"
                    >
                      <FileText className="w-4 h-4 text-[#2A5C5A]" /> {ml.my_cvs}
                    </button>
                  )}
                  {!user.is_premium && onOpenUpgrade && (
                    <button
                      onClick={() => { setMenuOpen(false); onOpenUpgrade(); }}
                      className="w-full text-left px-4 py-3 text-sm text-[#292524] hover:bg-[#FAF6F0] flex items-center gap-2 border-b border-[#F2EBE1] font-bold bg-gradient-to-r from-[#FDF7E6] to-white"
                      data-testid="header-upgrade-btn"
                    >
                      <Crown className="w-4 h-4 text-[#E5A93D]" /> Get Premium
                    </button>
                  )}
                  {onOpenRedeem && (
                    <button
                      onClick={() => { setMenuOpen(false); onOpenRedeem(); }}
                      className="w-full text-left px-4 py-3 text-sm text-[#292524] hover:bg-[#FAF6F0] flex items-center gap-2 border-b border-[#F2EBE1]"
                      data-testid="header-redeem-btn"
                    >
                      <Ticket className="w-4 h-4 text-[#D95D39]" /> {ml.redeem}
                    </button>
                  )}
                  {user.is_admin && onOpenAdmin && (
                    <button
                      onClick={() => { setMenuOpen(false); onOpenAdmin(); }}
                      className="w-full text-left px-4 py-3 text-sm text-[#292524] hover:bg-[#FAF6F0] flex items-center gap-2 border-b border-[#F2EBE1]"
                      data-testid="header-admin-btn"
                    >
                      <LayoutDashboard className="w-4 h-4 text-[#2A5C5A]" /> {ml.admin}
                    </button>
                  )}
                  <button
                    onClick={() => { setMenuOpen(false); onLogout?.(); }}
                    className="w-full text-left px-4 py-3 text-sm text-[#292524] hover:bg-[#FAF6F0] flex items-center gap-2"
                    data-testid="header-logout-btn"
                  >
                    <LogOut className="w-4 h-4 text-[#D9423E]" /> {ml.logout}
                  </button>
                </div>
              )}
            </div>
          ) : (
            onLogin && (
              <button
                onClick={onLogin}
                className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs font-semibold rounded-lg sm:rounded-xl bg-white border border-[#EAE5DB] text-[#292524] hover:border-[#292524] transition-colors whitespace-nowrap"
                data-testid="header-google-signin-btn"
                title="Sign in with Google"
                aria-label="Sign in with Google"
              >
                <svg width="14" height="14" viewBox="0 0 48 48" aria-hidden="true" className="sm:w-4 sm:h-4">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                </svg>
                <span>{ml.signin}</span>
              </button>
            )
          )}
        </div>
      </div>
    </header>
  );
}
